import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { route } from './HashRouter';
import { HelpModal } from './HelpModal';
import { Footer } from './shared/Footer';
import { GraphIcon, Logo, SetIcon, HomeIcon, HelpIcon, SettingsIcon } from './icons';
import { hasProAccess } from '../services/api';
import { useBilling } from '../contexts/BillingContext';
import { NotificationBell } from './NotificationBell';

type HelpPage = 'flat' | 'authority' | 'graph-viz' | 'graph' | 'vertex-sets' | 'vertex-set-detail' | 'archive' | 'settings' | null;

const helpContent: Record<Exclude<HelpPage, null>, { title: string; description: string; content: any }> = {
  flat: {
    title: 'Vertex List',
    description: 'A chronological list of all your saved vertices, sorted by most recently updated.',
    content: (
      <div class="help-content">
        <section>
          <h3>Search</h3>
          <p>Filter vertices by URL, title, description, or notes. Results update as you type.</p>
        </section>
        <section>
          <h3>View Mode</h3>
          <p>Toggle between flat list and grouped by authority (domain) using the grid icon below the search bar.</p>
        </section>
        <section>
          <h3>Vertex Cards</h3>
          <p>Each card represents a saved URL. Click the preview to open the original page.</p>
        </section>
        <section>
          <h3>Notes</h3>
          <p>Click on the note area to add or edit notes. Notes are searchable.</p>
        </section>
        <section>
          <h3>Sidebar Actions</h3>
          <dl>
            <dt>Archive pill</dt>
            <dd>Shows archived snapshots. Click to view past versions or enable auto-sharing.</dd>
            <dt>+ button</dt>
            <dd>Add this vertex to a set (collection) or create a new set.</dd>
            <dt>Copy icon</dt>
            <dd>Copy the URL to clipboard.</dd>
            <dt>Graph icon</dt>
            <dd>View this vertex's connections in graph view.</dd>
            <dt>Trash icon</dt>
            <dd>Delete this vertex (cannot be undone).</dd>
          </dl>
        </section>
      </div>
    ),
  },
  authority: {
    title: 'Authority View',
    description: 'Vertices grouped by domain (authority). Useful when you have many vertices and want to browse by source.',
    content: (
      <div class="help-content">
        <section>
          <h3>Authority Cards</h3>
          <p>Each card shows a domain with a preview image and vertex count. Cards are sorted by most recently updated.</p>
        </section>
        <section>
          <h3>Browsing</h3>
          <dl>
            <dt>Click a card</dt>
            <dd>View all vertices from that domain.</dd>
            <dt>"← Authorities" button</dt>
            <dd>Return to the domain grid from a filtered view.</dd>
            <dt>View mode toggle</dt>
            <dd>Switch back to flat list view using the grid icon.</dd>
          </dl>
        </section>
        <section>
          <h3>Search</h3>
          <p>Search still works in authority view. Results are grouped by domain, and empty groups are hidden.</p>
        </section>
      </div>
    ),
  },
  'graph-viz': {
    title: 'Graph Visualization',
    description: 'An interactive view of all your vertices and their connections, clustered by set or domain.',
    content: (
      <div class="help-content">
        <section>
          <h3>Navigation</h3>
          <dl>
            <dt>Pan</dt>
            <dd>Click and drag on empty space to move around.</dd>
            <dt>Zoom</dt>
            <dd>Scroll to zoom in and out.</dd>
            <dt>Drag nodes</dt>
            <dd>Click and drag a node to reposition it.</dd>
          </dl>
        </section>
        <section>
          <h3>Node Actions</h3>
          <p>Right-click a node to:</p>
          <dl>
            <dt>Connect to...</dt>
            <dd>Create an edge to another vertex. Click the target node to complete.</dd>
            <dt>View details</dt>
            <dd>Open the vertex's graph detail page.</dd>
            <dt>Open URL</dt>
            <dd>Open the original URL in a new tab.</dd>
          </dl>
        </section>
        <section>
          <h3>Edge Actions</h3>
          <p>Right-click an edge to delete the connection.</p>
        </section>
        <section>
          <h3>Clustering</h3>
          <p>Nodes are grouped by vertex set (colored) or by domain (grey). Cluster labels appear above each group.</p>
        </section>
      </div>
    ),
  },
  graph: {
    title: 'Vertex Graph',
    description: 'View and manage connections for a single vertex: what it links to and what links to it.',
    content: (
      <div class="help-content">
        <section>
          <h3>Connections</h3>
          <dl>
            <dt>Outgoing edges</dt>
            <dd>Vertices this URL links to (you created these connections).</dd>
            <dt>Incoming edges</dt>
            <dd>Vertices that link to this URL.</dd>
          </dl>
        </section>
        <section>
          <h3>Adding Connections</h3>
          <p>Use the autocomplete fields to add new outgoing or incoming edges. Type to search your vertices.</p>
        </section>
        <section>
          <h3>Navigation</h3>
          <p>Click on a connected vertex to navigate to its graph view. A breadcrumb trail tracks your path.</p>
        </section>
      </div>
    ),
  },
  'vertex-sets': {
    title: 'Vertex Sets',
    description: 'Organize vertices into named collections. Sets can be shared publicly.',
    content: (
      <div class="help-content">
        <section>
          <h3>Creating Sets</h3>
          <p>Click "New Set" to create a collection. Give it a name and optional description.</p>
        </section>
        <section>
          <h3>Set Cards</h3>
          <p>Each card shows the set name, vertex count, and preview images. Click to view details.</p>
        </section>
        <section>
          <h3>Adding Vertices</h3>
          <p>Add vertices to sets from the vertex list (+ button) or from within the set detail page.</p>
        </section>
      </div>
    ),
  },
  'vertex-set-detail': {
    title: 'Vertex Set',
    description: 'View and manage a single vertex set: its vertices, sharing settings, and unions.',
    content: (
      <div class="help-content">
        <section>
          <h3>Managing Vertices</h3>
          <dl>
            <dt>Add vertex</dt>
            <dd>Use the autocomplete to add existing vertices to this set.</dd>
            <dt>Remove vertex</dt>
            <dd>Click the X on a vertex card to remove it from the set.</dd>
          </dl>
        </section>
        <section>
          <h3>Sharing</h3>
          <p>Toggle sharing to generate a public link. Anyone with the link can view the set.
          <br/>
          If your set has unions with other sets, the public link followed by `/unions` can be used to view your set combined with its unions.</p>
        </section>
        <section>
          <h3>Unions</h3>
          <p>Union with other sets to display their vertices alongside this set's vertices.</p>
          <p>Search publicly shared sets by user: type the '@' followed by the user alias. Type further to search among the user's public sets.</p>
        </section>
      </div>
    ),
  },
  archive: {
    title: 'Archive Viewer',
    description: 'View archived snapshots of a webpage, with notes and sharing options.',
    content: (
      <div class="help-content">
        <section>
          <h3>Viewing Archives</h3>
          <p>The iframe shows the archived webpage as it appeared at the snapshot time.</p>
        </section>
        <section>
          <h3>Navigation</h3>
          <p>Use the dropdown to switch between available snapshots, sorted newest first.</p>
        </section>
        <section>
          <h3>Sharing</h3>
          <p>Toggle sharing to generate a public link for this specific archive snapshot.</p>
        </section>
        <section>
          <h3>Notes</h3>
          <p>Edit the note for this vertex directly from the archive viewer.</p>
        </section>
      </div>
    ),
  },
  settings: {
    title: 'Settings',
    description: 'Manage your account, API key, and user alias.',
    content: (
      <div class="help-content">
        <section>
          <h3>User Alias</h3>
          <p>Your public username for shared content. Click to edit.</p>
        </section>
        <section>
          <h3>API Key</h3>
          <dl>
            <dt>Rotate key</dt>
            <dd>Generate a new key. Your old key will stop working immediately.</dd>
            <dt>Copy key</dt>
            <dd>After rotating, copy the new key before leaving the page.</dd>
          </dl>
        </section>
        <section>
          <h3>Logout</h3>
          <p>Sign out of your account. You'll need your API key to log back in.</p>
        </section>
      </div>
    ),
  },
};

function getCurrentHelpPage(): HelpPage {
  const hash = window.location.hash;
  if (hash.startsWith('#/settings')) {
    return 'settings';
  } else if (hash.startsWith('#/archive/')) {
    return 'archive';
  } else if (hash.startsWith('#/graph-viz')) {
    return 'graph-viz';
  } else if (hash.startsWith('#/graph/')) {
    return 'graph';
  } else if (hash.match(/^#\/vertex-sets\/[^/]+/)) {
    return 'vertex-set-detail';
  } else if (hash.startsWith('#/vertex-sets')) {
    return 'vertex-sets';
  } else if (hash.includes('view=authority')) {
    return 'authority';
  } else if (hash.includes('view=flat') || hash === '#/' || hash === '') {
    return 'flat';
  }
  return null;
}

interface LayoutProps {
  children: any;
  showSearch?: boolean;
  onSearchChange?: (query: string) => void;
}

export function Layout({ children, showSearch = false, onSearchChange }: LayoutProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [helpPage, setHelpPage] = useState<HelpPage>(null);
  const [showHelp, setShowHelp] = useState(false);
  const { subscription, loading: billingLoading } = useBilling();
  const plan: 'loading' | 'free' | 'pro' =
    billingLoading ? 'loading' : hasProAccess(subscription) ? 'pro' : 'free';

  useEffect(() => {
    const updateHelpPage = () => setHelpPage(getCurrentHelpPage());
    updateHelpPage();
    window.addEventListener('hashchange', updateHelpPage);
    return () => window.removeEventListener('hashchange', updateHelpPage);
  }, []);

  const handleSearchInput = (e: Event) => {
    const value = (e.target as HTMLInputElement).value;
    setSearchQuery(value);
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  return (
    <div class="container">
      <header>
        <h1>
          <a
            onClick={() => route('/')}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
<Logo />
{plan === 'pro' && <span class="pro-badge">Pro</span>}
{plan === 'free' && <a class="upgrade-badge" href="/pricing">Upgrade</a>}
          </a>
        </h1>
        {showSearch ? (
          <input
            type="text"
            id="searchInput"
            placeholder="Search vertices..."
            value={searchQuery}
            onInput={handleSearchInput}
          />
        ) : (
          <div style={{ flex: 1 }} />
        )}
        <button
          class="btn-header-icon"
          title="Home"
          onClick={() => route('/')}
        >
<HomeIcon />
        </button>
        <button
          class="btn-header-icon"
          title="View full graph"
          onClick={() => route('/graph-viz')}
        >
<GraphIcon />
        </button>
        <button
          class="btn-header-icon"
          title="Vertex sets"
          onClick={() => route('/vertex-sets')}
        >
          <SetIcon size={18} strokeWidth={1.5} />
        </button>
        <NotificationBell />
        {helpPage && (
          <button
            class="btn-header-icon"
            title="Help"
            onClick={() => setShowHelp(true)}
          >
<HelpIcon />
          </button>
        )}
        <button
          class="btn-header-icon"
          title="Settings"
          onClick={() => route('/settings')}
        >
<SettingsIcon />
        </button>
      </header>

      {children}

      <Footer />

      {showHelp && helpPage && (
        <HelpModal
          title={helpContent[helpPage].title}
          description={helpContent[helpPage].description}
          onClose={() => setShowHelp(false)}
        >
          {helpContent[helpPage].content}
        </HelpModal>
      )}
    </div>
  );
}
